import React, { useState } from 'react';

import { PencilSimpleIcon } from '@phosphor-icons/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { useParams } from 'react-router-dom';
import { getModels } from 'api/models';

import { deleteAssistantArea, deleteAssistantFolder, deleteAssistantMcp, deleteAssistantOffice, deleteAssistantTag, getAssistant, updateAssistant } from 'api/assistants';

import { getGpts } from 'api/gpts';
import AssistantAddAreasPopup from 'components/Assistants/AssistantAddAreasPopup';
import AssistantAddFoldersPopup from 'components/Assistants/AssistantAddFoldersPopup';
import AssistantAddMCPPopup from 'components/Assistants/AssistantAddMCPPopup';
import AssistantAddOfficesPopup from 'components/Assistants/AssistantAddOfficesPopup';
import AssistantAddTagPopup from 'components/Assistants/AssistantAddTagPopup';
import Button from 'components/Button';
import Container from 'components/Container';
import SelectInput from 'components/Form/SelectInput';
import TextareaInput from 'components/Form/TextareaInput';
import TextInput from 'components/Form/TextInput';
import LoadingOverlay from 'components/LoadingOverlay';
import MobileHeader from 'components/MobileHeader';
import SearchOverlay from 'components/SearchOverlay';
import Sidebar from 'components/Sidebar';

export default function Edit() {

    const [sidebar, setSidebar] = useState(false);
    const [search, setSearch] = useState(false);
    const { assistantId } = useParams();
    const [addMcpPopup, setAddMcpPopup] = useState(false);
    const [addOfficePopup, setAddOfficePopup] = useState(false);
    const [addFolderPopup, setAddFolderPopup] = useState(false);
    const [addAreaPopup, setAddAreaPopup] = useState(false);
    const [addTagPopup, setAddTagPopup] = useState(false);
    const cache = useQueryClient();

    const { data: assistant, isLoading } = useQuery({
        queryKey: ['assistants', assistantId],
        queryFn: async () => await getAssistant({
            id: assistantId
        }),
    });

    const { data: modelsData } = useQuery({
        queryKey: ['models'],
        queryFn: async () => await getModels({}),
    });

    const modelOptions = modelsData
        ?.filter(m => m.enabled && m.type === 'model')
        ?.map(m => ({ value: m._id, label: m.model })) || [];

    const embeddingOptions = modelsData
        ?.filter(m => m.enabled && m.type === 'embedding')
        ?.map(m => ({ value: m._id, label: m.model })) || [];
    const { mutate: update } = useMutation({
        mutationKey: ['updateAssistant'],
        mutationFn: updateAssistant,
        onSuccess: () => {
            cache.invalidateQueries(['assistants', assistantId]);
        },
        onError: (error) => {
            console.error("Error updating assistant:", error);
        }
    });

    const { mutate: deleteArea } = useMutation({
        mutationKey: ['deleteAssistantArea'],
        mutationFn: deleteAssistantArea,
        onSuccess: () => {
            cache.invalidateQueries(['assistants', assistantId]);
        },
        onError: (error) => {
            console.error("Error removing Area from assistant:", error);
        }
    });
    function deleteAreaHandler(id) {
        return deleteArea({
            id: assistantId,
            areaId: id
        });
    }

    const { mutate: deleteMcp } = useMutation({
        mutationKey: ['deleteAssistantMcp'],
        mutationFn: deleteAssistantMcp,
        onSuccess: () => {
            cache.invalidateQueries(['assistants', assistantId]);
        },
        onError: (error) => {
            console.error("Error removing MCP from assistant:", error);
        }
    });
    function deleteMcpHandler(id) {
        return deleteMcp({
            id: assistantId,
            mcpId: id
        });
    }

    const { mutate: deleteTag } = useMutation({
        mutationKey: ['deleteAssistantTag'],
        mutationFn: deleteAssistantTag,
        onSuccess: () => {
            cache.invalidateQueries(['assistants', assistantId]);
        },
        onError: (error) => {
            console.error("Error removing tag from assistant:", error);
        }
    });
    function deleteTagHandler(id) {
        return deleteTag({
            id: assistantId,
            tagId: id
        });
    }

    const { mutate: deleteOffice } = useMutation({
        mutationKey: ['deleteAssistantOffice'],
        mutationFn: deleteAssistantOffice,
        onSuccess: () => {
            cache.invalidateQueries(['assistants', assistantId]);
        },
        onError: (error) => {
            console.error("Error removing office from assistant:", error);
        }
    });
    function deleteOfficeHandler(id) {
        return deleteOffice({
            id: assistantId,
            officeId: id
        });
    }

    const { mutate: deleteFolder } = useMutation({
        mutationKey: ['deleteAssistantFolder'],
        mutationFn: deleteAssistantFolder,
        onSuccess: () => {
            cache.invalidateQueries(['assistants', assistantId]);
        },
        onError: (error) => {
            console.error("Error removing folder from assistant:", error);
        }
    });
    function deleteFolderHandler(id) {
        return deleteFolder({
            id: assistantId,
            folderId: id
        });
    }

    function onSubmitHandler(formData) {
        return update({
            data: Object.fromEntries(formData),
            id: assistantId
        });
    }

    function onChangeImageHandler(e) {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                return update({
                    data: { image: file },
                    id: assistantId
                });
            };
            reader.readAsDataURL(file);
        }
    }


    const { isLoading: isLoadingGpt } = useQuery({
        queryKey: ['gpts'],
        queryFn: async () => await getGpts({}),
    })

    

    const publicOptions = [
        { value: true, label: 'Sì' },
        { value: false, label: 'No' }
    ];

    return (
        <>
            <Sidebar
                visible={sidebar}
                setVisible={setSidebar}
            />

            <SearchOverlay
                visible={search}
                setVisible={setSearch}
            />

            <MobileHeader
                sidebar={sidebar}
                setSidebar={setSidebar}
                search={search}
                setSearch={setSearch}
            />

            <LoadingOverlay isLoading={isLoading || isLoadingGpt} />

            <form action={onSubmitHandler}>
                <div className={`fixed top-0 w-full bottom-0 transition-all duration-300 ease-in-out lg:pl-[320px] overflow-y-auto ${sidebar ? 'left-[80vw]' : 'left-0'}`}>
                    <div className={`z-10 fixed top-0 w-[calc(100%-320px)] left-[320px] right-0 border-b border-black/6 py-3 px-6 flex justify-between items-center backdrop-blur-sm`}>
                        <div className='flex gap-2 items-center'>

                            <p className='text-lg leading-none'>{assistant?.name}</p>
                        </div>

                        <div>
                            <Button submit>Salva modifiche</Button>
                        </div>
                    </div>

                    <Container>
                        <div className='flex items-start gap-10 pt-24'>
                            <div className='w-[240px]'>
                                <div className='w-full px-6 py-12 bg-verylight border border-black/6 rounded-sm flex flex-col items-center justify-center'>
                                    <div className='relative'>
                                        {assistant?.image ?
                                            <img src={assistant?.image} alt={assistant?.name} className='w-28 h-28 object-cover rounded-full mb-4' />
                                            :
                                            <div className='w-28 h-28 bg-white border border-black/6 rounded-full mb-4' />
                                        }
                                        <div className='absolute w-7 h-7 bg-greydark rounded-full absolute top-1 right-1 flex items-center justify-center hover:bg-black cursor-pointer transition-colors duration-200'>
                                            <input type="file" className='absolute appearance-none top-0 left-0 bottom-0 right-0 opacity-0' onChange={(e) => onChangeImageHandler(e)} />
                                            <PencilSimpleIcon size={17} weight="bold" className='text-white m-1' />
                                        </div>
                                    </div>
                                    <p className='text-lg mb-2'>{assistant?.name}</p>
                                    <p className='text-sm text-muted text-center'>{assistant?.description}</p>
                                </div>
                                {assistant?.updatedAt &&
                                    <p className='text-xs text-muted pt-4 text-center'>Ultima modifica: {format(new Date(assistant.updatedAt), 'dd/MM/yyyy HH:mm')}</p>
                                }
                            </div>

                            <div className='flex-1 scroll-auto flex flex-col gap-4 pb-20'>
                                <div className='md:flex gap-5'>
                                    <TextInput
                                        label="Titolo"
                                        name="name"
                                        placeholder="Titolo"
                                        defaultValue={assistant?.name}
                                    />
                                </div>
                                <div className='md:flex gap-5'>
                                    <SelectInput
                                        label="Modello"
                                        name="model"
                                        options={modelOptions}
                                        defaultValue={assistant?.model}
                                    />
                                    <SelectInput
                                        label="Embedding"
                                        name="embedding"
                                        options={embeddingOptions}
                                        defaultValue={assistant?.embedding}
                                    />
                                </div>
                                <TextInput
                                    label="Descrizione"
                                    name="description"
                                    placeholder="Descrizione"
                                    defaultValue={assistant?.description}
                                />
                                <TextareaInput
                                    label="Istruzioni"
                                    name="instructions"
                                    placeholder="Istruzioni"
                                    defaultValue={assistant?.instructions}
                                />

                                <div className='md:flex gap-5'>
                                    <TextInput
                                        label="Baseurl"
                                        name="baseUrl"
                                        placeholder="Baseurl"
                                        defaultValue={assistant?.baseUrl}
                                    />

                                    <TextInput
                                        label="apiKey"
                                        name="apiKey"
                                        placeholder="apiKey"
                                        defaultValue={assistant?.apiKey}
                                    />
                                </div>
                                <div className='md:flex gap-5'>
                                    <SelectInput
                                        label="Assistente Pubblico"
                                        name="public"
                                        placeholder="Pubblico"
                                        options={publicOptions}
                                        defaultValue={assistant?.public ? true : false}
                                    />
                                </div>
                                <div className='pt-10'>
                                    <p className='text-sm block'>Cartelle collegate</p>
                                    <div className='pt-2'>
                                        {assistant?.folders?.map(({ folder, _id }) => {
                                            return (
                                                <div key={folder._id} className='w-full bg-verylight border border-light rounded-md px-4 py-3 mb-3'>
                                                    <p className='text-sm'>{folder.name}</p>
                                                    <p className='text-xs text-muted pt-1'>ID: {folder._id}</p>
                                                    <div className='hover:text-red-400 hover:underline cursor-pointer' onClick={() => deleteFolderHandler(_id)}>
                                                        Rimuovi
                                                    </div>
                                                </div>
                                            )
                                        })}
                                        <div className='mt-4 border border-dashed border-greylight rounded-sm hover:bg-verylight cursor-pointer transition-colors duration-200'>
                                            <div className='w-full px-4 py-3 flex justify-center' onClick={() => setAddFolderPopup(true)}>
                                                <p className='text-sm text-muted'>+ Aggiungi cartella</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className='pt-10'>
                                    <p className='text-sm block'>MCP Collegati</p>

                                    <div className='pt-2'>
                                        {assistant?.mcps?.map(({ mcp, _id }) => {
                                            return (
                                                <div key={mcp._id} className='w-full bg-verylight border border-light rounded-md px-4 py-3 mb-3'>
                                                    <p className='text-sm'>{mcp.name}</p>
                                                    <p className='text-xs text-muted pt-1'>ID: {mcp._id}</p>
                                                    <div className='hover:text-red-400 hover:underline cursor-pointer' onClick={() => deleteMcpHandler(_id)}>
                                                        Rimuovi
                                                    </div>
                                                </div>
                                            )
                                        })}
                                        <div className='mt-4 border border-dashed border-greylight rounded-sm hover:bg-verylight cursor-pointer transition-colors duration-200'>
                                            <div className='w-full px-4 py-3 flex justify-center' onClick={() => setAddMcpPopup(true)}>
                                                <p className='text-sm text-muted'>+ Aggiungi MCP</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className='pt-10'>
                                    <p className='text-sm block'>Aree Collegate</p>
                                    <div className='pt-2'>
                                        {assistant?.areas?.map(({ area, _id }) => {
                                            return (
                                                <div key={area._id} className='w-full bg-verylight border border-light rounded-md px-4 py-3 mb-3'>
                                                    <p className='text-sm'>{area.name}</p>
                                                    <p className='text-xs text-muted pt-1'>ID: {area._id}</p>
                                                    <div className='hover:text-red-400 hover:underline cursor-pointer' onClick={() => deleteAreaHandler(_id)}>
                                                        Rimuovi
                                                    </div>
                                                </div>
                                            )
                                        })}
                                        <div className='mt-4 border border-dashed border-greylight rounded-sm hover:bg-verylight cursor-pointer transition-colors duration-200'>
                                            <div className='w-full px-4 py-3 flex justify-center' onClick={() => setAddAreaPopup(true)}>
                                                <p className='text-sm text-muted'>+ Aggiungi Area</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className='pt-10'>
                                    <p className='text-sm block'>Uffici Collegati</p>
                                    <div className='pt-2'>
                                        {assistant?.offices?.map(({ office, _id }) => {
                                            return (
                                                <div key={office._id} className='w-full bg-verylight border border-light rounded-md px-4 py-3 mb-3'>
                                                    <p className='text-sm'>{office.name}</p>
                                                    <p className='text-xs text-muted pt-1'>ID: {office._id}</p>
                                                    <div className='hover:text-red-400 hover:underline cursor-pointer' onClick={() => deleteOfficeHandler(_id)}>
                                                        Rimuovi
                                                    </div>
                                                </div>
                                            )
                                        })}
                                        <div className='mt-4 border border-dashed border-greylight rounded-sm hover:bg-verylight cursor-pointer transition-colors duration-200'>
                                            <div className='w-full px-4 py-3 flex justify-center' onClick={() => setAddOfficePopup(true)}>
                                                <p className='text-sm text-muted'>+ Aggiungi Ufficio</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className='pt-10'>
                                    <p className='text-sm block'>Tag Collegati</p>
                                    <div className='pt-2'>
                                        {assistant?.tags?.map(({ tag, _id }) => {
                                            return (
                                                <div key={tag._id} className='w-full bg-verylight border border-light rounded-md px-4 py-3 mb-3'>
                                                    <p className='text-sm'>{tag.name}</p>
                                                    <p className='text-xs text-muted pt-1'>ID: {tag._id}</p>
                                                    <div className='hover:text-red-400 hover:underline cursor-pointer' onClick={() => deleteTagHandler(_id)}>
                                                        Rimuovi
                                                    </div>
                                                </div>
                                            )
                                        })}
                                        <div className='mt-4 border border-dashed border-greylight rounded-sm hover:bg-verylight cursor-pointer transition-colors duration-200'>
                                            <div className='w-full px-4 py-3 flex justify-center' onClick={() => setAddTagPopup(true)}>
                                                <p className='text-sm text-muted'>+ Aggiungi Tag</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Container>
                </div>
            </form>


            <AssistantAddFoldersPopup
                visible={addFolderPopup}
                setVisible={setAddFolderPopup}
                present={assistant?.folders?.map(folder => folder.folder._id)}
            />
            <AssistantAddMCPPopup
                visible={addMcpPopup}
                setVisible={setAddMcpPopup}
                present={assistant?.mcps?.map(mcp => mcp.mcp._id)}
            />

            <AssistantAddOfficesPopup
                visible={addOfficePopup}
                setVisible={setAddOfficePopup}
                present={assistant?.offices?.map(office => office.office._id)}
            />

            <AssistantAddAreasPopup
                visible={addAreaPopup}
                setVisible={setAddAreaPopup}
                present={assistant?.areas?.map(area => area.area._id)}
            />

            <AssistantAddTagPopup
                visible={addTagPopup}
                setVisible={setAddTagPopup}
                present={assistant?.tags?.map(tag => tag.tag._id)}
            />
        </>
    )
}
